import React, { useEffect, useState } from 'react';
import { FileBarChart, Loader2, Save, ListChecks } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const ANCHOR_LABELS = {
  report_month_end: 'Report month-end (the last day of the report month)',
  today: 'Today (the date the report is generated)',
};

const HRReportSettingsTab = ({ isViewer = false }) => {
  const [resignedDays, setResignedDays] = useState('45');
  const [eosbDays, setEosbDays] = useState('180');
  const [anchor, setAnchor] = useState('report_month_end');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get('/api/settings/hr-report-windows')
      .then((r) => {
        if (!mounted) return;
        const c = r.data?.current || {};
        setResignedDays(String(c.resigned_window_days ?? 45));
        setEosbDays(String(c.eosb_paid_window_days ?? 180));
        setAnchor(c.window_anchor || 'report_month_end');
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const save = async () => {
    const rd = parseInt(resignedDays, 10);
    const ed = parseInt(eosbDays, 10);
    if (!Number.isInteger(rd) || rd < 1 || rd > 3650 || !Number.isInteger(ed) || ed < 1 || ed > 3650) {
      toast({ title: 'Invalid values', description: 'Windows must be whole numbers between 1 and 3650 days.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/api/settings/hr-report-windows', {
        resigned_window_days: rd,
        eosb_paid_window_days: ed,
        window_anchor: anchor,
      });
      const c = res.data?.current || {};
      setResignedDays(String(c.resigned_window_days ?? rd));
      setEosbDays(String(c.eosb_paid_window_days ?? ed));
      setAnchor(c.window_anchor || anchor);
      toast({ title: 'HR Report settings saved', description: 'Future HR Reports will use these recency windows.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Could not save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
    <Card data-testid="hr-report-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileBarChart className="h-5 w-5 text-amber-600" />
          HR Report Windows
        </CardTitle>
        <p className="text-xs text-gray-500">
          Control how recent an exit or settlement must be to appear in the HR Report's
          "Resigned/Terminated" and "EOSB Fully Paid" sections.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading current settings…
          </div>
        ) : (
          <div className="space-y-5 max-w-md">
            <div>
              <Label className="text-xs">Resigned / Terminated window (days)</Label>
              <Input
                type="number"
                min={1}
                max={3650}
                value={resignedDays}
                onChange={(e) => setResignedDays(e.target.value)}
                disabled={isViewer}
                data-testid="hr-report-resigned-days-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Default 45. Staff who resigned or were terminated within this many days appear in the
                "Resigned/Terminated (Last N Days)" section.
              </p>
            </div>

            <div>
              <Label className="text-xs">EOSB Fully Paid window (days)</Label>
              <Input
                type="number"
                min={1}
                max={3650}
                value={eosbDays}
                onChange={(e) => setEosbDays(e.target.value)}
                disabled={isViewer}
                data-testid="hr-report-eosb-days-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Default 180. Fully-settled exits older than this are dropped from the "EOSB Fully Paid"
                section. Pending/partial EOSB always stays visible.
              </p>
            </div>

            <div>
              <Label className="text-xs">Window anchor</Label>
              <Select value={anchor} onValueChange={setAnchor} disabled={isViewer}>
                <SelectTrigger data-testid="hr-report-anchor-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="report_month_end">Report month-end</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">{ANCHOR_LABELS[anchor]}</p>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving || isViewer}
                className="bg-amber-600 hover:bg-amber-700 gap-1.5"
                data-testid="hr-report-settings-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    <HRReportSectionsCard isViewer={isViewer} />
    <HRCoverContactCard isViewer={isViewer} />
    </div>
  );
};

const HRCoverContactCard = ({ isViewer = false }) => {
  const [c, setC] = useState({ name: '', designation: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/api/settings/hr-cover-contact').then((r) => setC({
      name: r.data?.name || '', designation: r.data?.designation || '',
      phone: r.data?.phone || '', email: r.data?.email || '',
    })).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/hr-cover-contact', c);
      toast({ title: 'Cover contact saved', description: 'Shown on the portal when all HR staff are on leave.' });
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <Card data-testid="hr-cover-contact-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="h-5 w-5 text-amber-600" /> HR Cover Contact
        </CardTitle>
        <p className="text-xs text-gray-500">
          Fallback person shown on every employee's portal ("contact this person instead") when all HR staff are on leave.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} placeholder="e.g. Operations Manager" data-testid="cover-name" /></div>
          <div><Label className="text-xs">Designation</Label><Input value={c.designation} onChange={(e) => setC({ ...c, designation: e.target.value })} placeholder="e.g. GM" data-testid="cover-designation" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} placeholder="+971 ..." data-testid="cover-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} placeholder="name@company.com" data-testid="cover-email" /></div>
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={save} disabled={saving || isViewer} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="cover-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save contact
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const HRReportSectionsCard = ({ isViewer = false }) => {
  const [available, setAvailable] = useState([]);
  const [current, setCurrent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get('/api/settings/hr-report-sections')
      .then((r) => {
        if (!mounted) return;
        setAvailable(r.data?.available || []);
        setCurrent(r.data?.current || {});
      })
      .catch(() => { /* keep empty */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const toggle = (key) => setCurrent((c) => ({ ...c, [key]: !c[key] }));
  const setAll = (val) => setCurrent(Object.fromEntries(available.map((s) => [s.key, val])));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/api/settings/hr-report-sections', { sections: current });
      setCurrent(res.data?.current || current);
      toast({ title: 'Report sections saved', description: 'Future HR Reports (including the scheduled email) will only include the ticked sections.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Could not save sections', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const onCount = available.filter((s) => current[s.key]).length;

  return (
    <Card data-testid="hr-report-sections-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="h-5 w-5 text-indigo-600" />
          HR Report Sections
        </CardTitle>
        <p className="text-xs text-gray-500">
          Tick which sections to include in the monthly HR Report. Unticked sections are left out of both
          the on-screen report and the scheduled auto-email. New sections default to ON.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sections…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500" data-testid="hr-report-sections-count">
                {onCount} of {available.length} sections included
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={isViewer} onClick={() => setAll(true)} data-testid="hr-report-sections-all">Select all</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={isViewer} onClick={() => setAll(false)} data-testid="hr-report-sections-none">Clear all</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {available.map((s) => (
                <label
                  key={s.key}
                  htmlFor={`hr-section-${s.key}`}
                  className="flex items-center justify-between py-2 border-b border-gray-100 cursor-pointer"
                >
                  <span className="text-sm text-gray-700">{s.label}</span>
                  <Switch
                    id={`hr-section-${s.key}`}
                    checked={!!current[s.key]}
                    onCheckedChange={() => toggle(s.key)}
                    disabled={isViewer}
                    data-testid={`hr-section-toggle-${s.key}`}
                  />
                </label>
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving || isViewer}
                className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"
                data-testid="hr-report-sections-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save sections
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HRReportSettingsTab;
